import { isDateClause, matchesDateClause } from './date-filter.js';
import { isAmountClause, matchesAmountClause } from './amount-filter.js';

export { buildDateClause, getDatePresetBounds, isDateClause, matchesDateClause, parseDateClause, DATE_PRESET_VALUES } from './date-filter.js';
export { buildAmountClause, isAmountClause, matchesAmountClause, parseAmountClause, AMOUNT_OPERATORS, stripAmountInput } from './amount-filter.js';

/**
 * @param {*} value
 * @returns {string}
 */
export function normalizeText(value) {
	return String(value ?? '').toLowerCase();
}

/**
 * @param {{ getValue: (item: any) => any } | null | undefined} field
 * @param {*} item
 * @returns {string}
 */
export function extractFieldValue(field, item) {
	if (!field) { return ''; }
	try { return String(field.getValue(item)); } catch { return ''; }
}

/**
 * @param {{ filterFields: Array<{ key: string, type?: string, getValue: (item: any) => any, normalizeInput?: (value: string) => string }> }} config
 * @param {string} fieldKey
 * @returns {{ key: string, type?: string, getValue: (item: any) => any, normalizeInput?: (value: string) => string } | null}
 */
export function getFilterField(config, fieldKey) {
	return (config.filterFields ?? []).find((/** @type {{ key: string }} */ field) => field.key === fieldKey) ?? null;
}

/**
 * @param {*} item
 * @param {{ fieldKey: string, value: string }} clause
 * @param {{ filterFields: Array<{ key: string, type?: string, getValue: (item: any) => any, normalizeInput?: (value: string) => string }> }} config
 * @returns {boolean}
 */
export function matchesClause(item, clause, config) {
	if (!clause.value) { return true; }
	if (clause.fieldKey === '*') {
		const needle = normalizeText(clause.value);
		return (config.filterFields ?? []).some((field) => {
			if (field.type === 'date' && isDateClause(clause.value)) {
				const haystack = extractFieldValue(field, item);
				return matchesDateClause(haystack, clause.value);
			}
			if (field.type === 'currency' && isAmountClause(clause.value)) {
				try {
					const raw = field.getValue(item);
					const num = Number(raw);
					if (Number.isFinite(num)) { return matchesAmountClause(num, clause.value, item); }
				} catch { /* ignore */ }
				return false;
			}
			const haystack = normalizeText(extractFieldValue(field, item));
			return haystack.includes(needle);
		});
	}
	const field = getFilterField(config, clause.fieldKey);
	if (!field) { return true; }
	if (field.type === 'date' && isDateClause(clause.value)) {
		const haystack = extractFieldValue(field, item);
		return matchesDateClause(haystack, clause.value);
	}
	if (field.type === 'currency' && isAmountClause(clause.value)) {
		try {
			const raw = field.getValue(item);
			const num = Number(raw);
			if (Number.isFinite(num)) { return matchesAmountClause(num, clause.value, item); }
		} catch { /* ignore */ }
		return false;
	}
	const needle = normalizeText(clause.value);
	const haystack = normalizeText(extractFieldValue(field, item));
	if (haystack.includes(needle)) { return true; }
	if (field.normalizeInput) {
		const cleaned = normalizeText(field.normalizeInput(clause.value));
		if (cleaned && cleaned !== needle && haystack.includes(cleaned)) { return true; }
	}
	return false;
}

/**
 * @param {any[]} items
 * @param {Array<{ fieldKey: string, value: string }>} clauses
 * @param {{ filterFields: Array<any> }} config
 * @returns {any[]}
 */
export function filterItems(items, clauses, config) {
	if (!clauses || clauses.length === 0) { return items; }
	return items.filter((item) => {
		/** @type {Record<string, Array<{ fieldKey: string, value: string }>>} */
		const groups = {};
		for (const clause of clauses) {
			if (!groups[clause.fieldKey]) { groups[clause.fieldKey] = []; }
			const g = groups[clause.fieldKey];
			if (g) { g.push(clause); }
		}
		return Object.values(groups).every((group) =>
			group.some((clause) => matchesClause(item, clause, config))
		);
	});
}

/**
 * Derive filter field configs from column definitions.
 * Columns without `getValue` or with `filterable: false` are excluded.
 *
 * @param {Array<{ label: string, key?: string, type?: string, getValue?: (item: any) => any, getTags?: (item: any) => string[], filterable?: boolean, filterType?: string, filterOptions?: string[], normalizeInput?: (value: string) => string }>} columns
 * @returns {Array<{ key: string, label: string, type: string, options: string[], getValue: (item: any) => string, getTags?: (item: any) => string[], normalizeInput?: (value: string) => string }>}
 */
export function deriveFilterFields(columns) {
	const cols = /** @type {Array<{ label: string, key?: string, type?: string, filterType?: string, filterOptions?: string[], getValue: (item: any) => any, getTags?: (item: any) => string[], normalizeInput?: (value: string) => string }>} */ (
		(columns ?? []).filter((/** @type {{ getValue?: any, filterable?: boolean }} */ col) => col.getValue && col.filterable !== false)
	);
	return cols.map((col) => {
		const derivedType = col.filterType ?? (col.type === 'date' ? 'date' : col.type === 'currency' ? 'currency' : 'text');
		/** @type {{ key: string, label: string, type: string, options: string[], getValue: (item: any) => string, getTags?: (item: any) => string[], normalizeInput?: (value: string) => string }} */
		const field = {
			key: col.key ?? col.label.toLowerCase(),
			label: col.label,
			type: derivedType,
			options: col.filterOptions ?? [],
			getValue: (/** @type {any} */ item) => String(col.getValue(item)),
		};
		if (col.getTags) {
			field.getTags = col.getTags;
		}
		if (col.normalizeInput) {
			field.normalizeInput = col.normalizeInput;
		}
		return field;
	});
}
